import type { MouseEvent } from "react";
import { StateMessage } from "@/components/StateMessage";
import { useDueReminders } from "@/hooks/useDueReminders";
import { reminderPhrase } from "@/lib/reminderPhrase";
import "./RemindersTile.css";

const MANAGE_HREF = "/farm/equipment";

/** Plain left click navigates client-side via the History API (same as Farm.tsx's cards); modified clicks keep native link behaviour. */
function onManageClick(event: MouseEvent<HTMLAnchorElement>): void {
  if (event.defaultPrevented || event.button !== 0
    || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }
  event.preventDefault();
  window.history.pushState({}, "", MANAGE_HREF);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * ef-061-9 — the Home "Reminders" tile: what is due or coming due on the farm's machines, read-only.
 * Fetches its own data (`useDueReminders`); the tile wrapper (`HomeTile`) owns the test id.
 * The due sentence is `reminderPhrase()` — never re-worded here. Archived machines' reminders never
 * arrive from the server, so nothing here filters them.
 */
export function RemindersTile() {
  const { reminders, status, error } = useDueReminders();
  const total = reminders ? reminders.dueCount + reminders.soonCount : 0;
  const isEmpty = status === "ready" && total === 0;
  const dataState = isEmpty ? "empty" : status;
  const more = reminders ? total - reminders.items.length : 0;

  return (
    <section className="reminders-tile" role="region" aria-label="Reminders" data-state={dataState}>
      <div className="reminders-tile__head">
        <h2 className="reminders-tile__title">Reminders</h2>
        {status === "ready" && reminders && !isEmpty && (
          <p className="reminders-tile__counts">
            <span>{reminders.dueCount} due</span>
            {" · "}
            <span>{reminders.soonCount} soon</span>
          </p>
        )}
      </div>

      {status === "loading" && <StateMessage tone="loading" title="Loading reminders…" />}

      {status === "error" && (
        <StateMessage tone="error" title="Couldn't load reminders." detail={error ?? undefined} />
      )}

      {isEmpty && <StateMessage tone="empty" title="Nothing due." />}

      {status === "ready" && reminders && !isEmpty && (
        <>
          <ul className="reminders-tile__list" aria-label="Reminders due">
            {reminders.items.map((r) => (
              <li key={r.id} className="reminders-tile__item" data-state={r.state}>
                <span className="reminders-tile__item-title">{r.title}</span>
                {r.equipmentName && <span className="reminders-tile__item-equipment">{r.equipmentName}</span>}
                <span className="reminders-tile__item-phrase">{reminderPhrase(r)}</span>
              </li>
            ))}
          </ul>
          {more > 0 && <p className="reminders-tile__more">+{more} more</p>}
        </>
      )}

      {(status === "ready") && (
        <a className="reminders-tile__link" href={MANAGE_HREF} onClick={onManageClick}>
          Manage reminders
        </a>
      )}
    </section>
  );
}
